import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { ToastModule } from 'primeng/toast';
import {
    FileSelectEvent,
    FileUploadEvent,
    FileUploadModule,
} from 'primeng/fileupload';
import { InsuranceProvidersService } from 'src/app/demo/service/insurance-providers.service';
import { forkJoin, switchMap } from 'rxjs';
import { CreateProviderRequest, Provider } from 'src/app/demo/api/provider';
import { NgxTranslateModule } from 'src/app/translate.module';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/demo/service/language.service';
import { languages } from 'src/app/demo/constants/languages';
import { showErrorToast } from 'src/app/demo/utilities/toast-utils';

@Component({
    selector: 'app-create-provider',
    standalone: true,
    imports: [
        CommonModule,
        CardModule,
        FormsModule,
        NgxTranslateModule,
        ReactiveFormsModule,
        FileUploadModule,
        ToastModule,
        InputTextModule,
        TooltipModule,
        MessagesModule,
        ButtonModule,
    ],
    templateUrl: './create-provider.component.html',
    styleUrl: './create-provider.component.scss',
    providers: [MessageService],
})
export class CreateProviderComponent {
    providerForm!: FormGroup;
    logoForm!: FormGroup;
    selectedFile!: any;
    provider!: Provider;
    providerRequest!: CreateProviderRequest;
    binaryString!: string;

    languages: any[] = languages;
    selectedLanguage!: string;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private translateService: TranslateService,
        private languageService: LanguageService,
        private providerService: InsuranceProvidersService,
        private progressLoader: ProgressLoaderService,
        private messageService: MessageService
    ) {
        this.providerForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(3)]],
        });

        this.logoForm = this.fb.group({
            contents: [null],
        });
    }

    ngOnInit(): void {
        this.initializeLocale();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    onFileSelect(event: FileSelectEvent) {
        this.selectedFile = event.currentFiles[0];
    }

    async onSaveSelected() {
        this.providerRequest = {
            providerName: this.providerForm.getRawValue().name,
        };
        let arrayBuffer: ArrayBuffer;

        if (this.selectedFile) {
            this.binaryString = await this.fileToBinaryString(
                this.selectedFile
            );
            this.createSubsequentRequests();
        } else {
            this.createProvider();
        }
    }

    fileToBinaryString(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                if (event.target?.result) {
                    const arrayBuffer = event.target.result as ArrayBuffer;
                    const bytes = new Uint8Array(arrayBuffer);
                    let binaryString = '';

                    for (let i = 0; i < bytes.byteLength; i++) {
                        binaryString += String.fromCharCode(bytes[i]);
                    }

                    resolve(binaryString);
                } else {
                    reject(new Error('Failed to read file'));
                }
            };

            reader.onerror = (error) => {
                reject(error);
            };
            reader.readAsArrayBuffer(file);
        });
    }

    uploadLogo() {
        this.providerService.uploadLogo(this.provider.id, {contents: this.binaryString}).subscribe({
            next: (value) => {
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
            },
        });
    }

    createProvider() {
        this.providerService.createProvider(this.providerRequest).subscribe({
            next: (value) => {
                this.provider = value;
                this.uploadLogo();
                this.router.navigate(['/insurance-providers']);
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
            },
        });
    }

    async createSubsequentRequests() {
        this.binaryString = await this.fileToBinaryString(this.selectedFile);
        this.providerService
            .createProvider(this.providerRequest)
            .pipe(
                switchMap((firstResponse) => {
                    this.provider = firstResponse;

                    return this.providerService.uploadLogo(
                        this.provider.id,
                        {contents: this.binaryString}
                    );
                })
            )
            .subscribe({
                next: (value) => {
                },
                error: (err) => {
                    showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                },
            });
    }

    back(){
        this.router.navigate(['/home/insurance-providers']);
    }
}
